import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { LOCATIONS, USER_CONTEXT } from '../../constants';
import {
  getFormDefinitionById,
  listFormDefinitions,
  listFormSubmissions,
  updateFormDefinition,
} from '../../util/APIUtils';
import { EVENTS } from '../services/constants';
import { eventEmitter } from '../services/event-emitter-service';
import store, { showToast } from '../core/store';
import {
  FORM_SUBMISSIONS_PAGE_SIZE,
  buildFormSubmissionsColumns,
  buildFormSubmissionsTdStyles,
  collectSortedDataKeysFromSubmissions,
  extractSignatureKeysFromFormDefinition,
  getLocationFilterOptions,
  isFormIdPresent,
  mapFormSubmissionsToGridData,
} from './utils/formSubmissions.util';
import { downloadCsv } from './utils/csvExport.util';
import {
  buildSubmissionsCsvExportPayload,
  normalizeSubmissionsResponse,
} from './utils/formSubmissionsExport.util';
import {
  FEEDS_FORMS_TAB_ROUTE,
  FORM_SUBMISSIONS_LOAD_ERROR,
} from './constants/formSubmissions.constants';
import { FORMS_UPDATE_ERROR, formEditRoute } from './constants/forms.constants';
import { buildEditAction, buildPreviewFormAction, buildToggleAction } from './utils/form.util';
import { mapApiFormDefinitionToForm } from './utils/forms.util';

export const useFormSubmissionsHook = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const currentPersonId = USER_CONTEXT?.personId;
  const [formTitle, setFormTitle] = useState('');
  const [formDefinition, setFormDefinition] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  /** Bumps when global `LOCATIONS` is populated so location filter options stay in sync. */
  const [locationFilterRevision, setLocationFilterRevision] = useState(0);
  const [previewForm, setPreviewForm] = useState(null);

  const bumpLocationFilterRevision = useCallback(() => {
    setLocationFilterRevision((n) => n + 1);
  }, []);

  const resetSubmissionsState = useCallback(() => {
    setFormDefinition(null);
    setSubmissions([]);
    setLoading(false);
  }, []);

  const handleLoadFailure = useCallback(() => {
    setFormTitle('');
    setFormDefinition(null);
    setSubmissions([]);
    store.dispatch(showToast(FORM_SUBMISSIONS_LOAD_ERROR));
  }, []);

  const fetchSubmissionsForForm = useCallback(async () => {
    const submissionPayload = await listFormSubmissions(formId);
    let definition = null;
    try {
      definition = await getFormDefinitionById(formId);
    } catch (e) {
      if (e?.message === 'INACTIVE_FORM') {
        const listRes = await listFormDefinitions();
        const raw = listRes?.data ?? listRes;
        const rows = Array.isArray(raw) ? raw : raw?.content;
        definition = Array.isArray(rows)
          ? rows.find((f) => String(f.id) === String(formId)) ?? null
          : null;
      } else {
        throw e;
      }
    }
    setFormTitle(String(definition?.name ?? ''));
    setFormDefinition(definition ?? null);
    setSubmissions(normalizeSubmissionsResponse(submissionPayload));
    bumpLocationFilterRevision();
  }, [formId, bumpLocationFilterRevision]);

  const load = useCallback(async () => {
    if (!isFormIdPresent(formId)) {
      resetSubmissionsState();
      return;
    }
    setLoading(true);
    try {
      await fetchSubmissionsForForm();
    } catch {
      handleLoadFailure();
    } finally {
      setLoading(false);
    }
  }, [formId, resetSubmissionsState, fetchSubmissionsForForm, handleLoadFailure]);

  const dataKeys = useMemo(
    () => collectSortedDataKeysFromSubmissions(submissions),
    [submissions],
  );

  const signatureKeys = useMemo(
    () => extractSignatureKeysFromFormDefinition(formDefinition),
    [formDefinition],
  );

  const handleExportSubmissions = useCallback(() => {
    downloadCsv(
      buildSubmissionsCsvExportPayload({ submissions, signatureKeys, formTitle }),
    );
  }, [submissions, signatureKeys, formTitle]);

  const locationFilterOptions = useMemo(
    () => getLocationFilterOptions(LOCATIONS),
    // LOCATIONS is module-level; `locationFilterRevision` bumps when it is populated (event / load).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationFilterRevision],
  );

  const columns = useMemo(
    () => buildFormSubmissionsColumns(submissions, signatureKeys, locationFilterOptions),
    [submissions, signatureKeys, locationFilterOptions],
  );

  const gridData = useMemo(
    () => mapFormSubmissionsToGridData(submissions, dataKeys, signatureKeys),
    [submissions, dataKeys, signatureKeys],
  );

  const tdStyles = useMemo(() => buildFormSubmissionsTdStyles(columns), [columns]);

  const handleBackToForms = useCallback(() => {
    navigate(FEEDS_FORMS_TAB_ROUTE);
  }, [navigate]);

  const formForActions = useMemo(() => mapApiFormDefinitionToForm(formDefinition), [formDefinition]);

  const clearPreviewForm = useCallback(() => {
    setPreviewForm(null);
  }, []);

  const handlePreviewForm = useCallback((form) => {
    setPreviewForm(form);
  }, []);

  const handleEditFormFromMenu = useCallback(
    (form) => {
      if (form?.id == null || form.id === '') {
        return;
      }
      navigate(formEditRoute(form.id));
    },
    [navigate],
  );

  const handleToggleActiveFromMenu = useCallback(
    async (form) => {
      setLoading(true);
      try {
        await updateFormDefinition(form.id, { is_active: !form.active });
        await fetchSubmissionsForForm();
      } catch {
        store.dispatch(showToast(FORMS_UPDATE_ERROR));
      } finally {
        setLoading(false);
      }
    },
    [fetchSubmissionsForForm],
  );

  const formActionsDropdownOptions = useMemo(() => {
    const form = formForActions;
    if (form == null) {
      return [];
    }
    const items = [
      buildPreviewFormAction(form, handlePreviewForm),
      buildEditAction(form, handleEditFormFromMenu),
    ];
    const toggle = buildToggleAction(form, handleToggleActiveFromMenu, currentPersonId);
    if (toggle) {
      items.push(toggle);
    }
    return items.map((action) => ({
      label: action.title,
      value: action.id,
      onClick: action.onClick,
      icon: action.icon,
    }));
  }, [
    formForActions,
    handlePreviewForm,
    handleEditFormFromMenu,
    handleToggleActiveFromMenu,
    currentPersonId,
  ]);

  const primaryButtonProps = useMemo(
    () => ({
      text: 'Actions',
      endIcon: <ArrowDropDownIcon />,
      dropdownOptions: formActionsDropdownOptions,
      show: formActionsDropdownOptions.length > 0,
    }),
    [formActionsDropdownOptions],
  );

  const secondaryButtonProps = useMemo(
    () => ({
      text: 'Export CSV',
      variant: 'outlined',
      startIcon: <SaveAltIcon />,
      onClick: handleExportSubmissions,
      disabled: loading || submissions.length === 0,
    }),
    [handleExportSubmissions, loading, submissions.length],
  );

  const subscribeToLocationsFetched = useCallback(() => {
    eventEmitter.on(EVENTS.LOCATIONS_FETCHED, bumpLocationFilterRevision);
    return () => {
      eventEmitter.off(EVENTS.LOCATIONS_FETCHED, bumpLocationFilterRevision);
    };
  }, [bumpLocationFilterRevision]);

  useEffect(subscribeToLocationsFetched, [subscribeToLocationsFetched]);
  useEffect(() => {
    load();
  }, [load]);

  return {
    formId,
    formTitle,
    formForActions,
    currentPersonId,
    columns,
    gridData,
    tdStyles,
    pageSize: FORM_SUBMISSIONS_PAGE_SIZE,
    loading,
    refreshSubmissions: load,
    handleBackToForms,
    primaryButtonProps,
    secondaryButtonProps,
    previewForm,
    clearPreviewForm,
    handlePreviewForm,
    handleEditFormFromMenu,
    handleToggleActiveFromMenu,
  };
};
